import React, { useEffect, useState } from 'react';

import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EmptyBox } from '../../../../../assets/icons/empty_box.svg';
import { ReactComponent as InfoIcon } from '../../../../../assets/icons/info-icon-ne.svg';
import { ReactComponent as RedArrow } from '../../../../../assets/icons/north_east.svg';
import { ReactComponent as GreenArrow } from '../../../../../assets/icons/south_west.svg';
import CustomEmptyPage from '../../../../../shared/components/CustomEmptyPage/CustomEmptyPage';
import EmptyDash from '../../../../../shared/components/EmptyDash/EmptyDash';
import StyledTable from '../../../../../shared/components/StyledTable/StyledTable';
import TablePagination from '../../../../../shared/components/TablePaginationNE/TablePaginationNE';
import Tag from '../../../../../shared/components/Tag/Tag';
import {
  REWARDS_ORDER_STATUS_FILTER,
  REWARDS_ORDER_STATUS_TAG_TYPES,
  REWARDS_ORDER_TYPES
} from '../../../../../shared/constants/rewards';
import { DEFAULT_PAGINATOR_STATE } from '../../../../../shared/constants/settings';
import { SECTION_REWARDS_HISTORY } from '../../../../../shared/constants/tagManager';
import { useIsMounted } from '../../../../../shared/hooks/useIsMounted';
import { IRewardsOrder } from '../../../../../shared/interfaces/rewards';
import { ITableColumn } from '../../../../../shared/interfaces/tables';
import { TRANSLATION_HISTORY } from '../../../../../shared/translations/rewards';
import { RewardsOrderTypes } from '../../../../../shared/types/rewards';
import { invertDateFormat } from '../../../../../shared/utils/utils';

import './RewardsHistoryTable.scss';

interface RewardsHistoryTableProps {
  ordersList: IRewardsOrder[] | undefined;
  error: boolean;
  loading: boolean;
  orderType: RewardsOrderTypes;
}

export const REWARDS_OUTGOINGS_TABLE_COLUMNS = [
  {
    name: TRANSLATION_HISTORY.table.table.header.details,
    key: 'transactionDetail',
    class: 'table-hide-mobile'
  },
  {
    name: TRANSLATION_HISTORY.table.table.header.order,
    key: 'orderNumber',
    class: 'table-hide-tablet'
  },
  {
    name: TRANSLATION_HISTORY.table.table.header.responsable,
    key: 'email',
    class: 'table-hide-mobile'
  },
  {
    name: TRANSLATION_HISTORY.table.table.header.sendDate,
    key: 'createdAt',
    class: 'table-hide-tablet',
    isSortable: true,
    render: (order: IRewardsOrder) => {
      return <>{order.createdAt ? order.createdAt.split('T')[0] : <EmptyDash />}</>;
    }
  },
  {
    name: TRANSLATION_HISTORY.table.table.header.status,
    key: 'status',
    class: 'table-hide-tablet'
  },
  {
    name: TRANSLATION_HISTORY.table.table.header.amount,
    key: 'amount',
    class: 'w-1',
    render: (order: IRewardsOrder) => {
      return (
        <div className="flex flex-row items-center gap-1">
          <RedArrow />
          {order.amount}
        </div>
      );
    }
  }
];

export const REWARDS_DEPOSITS_TABLE_COLUMNS = [
  {
    name: TRANSLATION_HISTORY.table.table.header.details,
    key: 'transactionDetail',
    class: 'table-hide-mobile'
  },
  {
    name: TRANSLATION_HISTORY.table.table.header.order,
    key: 'orderNumber',
    class: 'table-hide-tablet'
  },
  {
    name: TRANSLATION_HISTORY.table.table.header.responsable,
    key: 'email',
    class: 'table-hide-mobile'
  },
  {
    name: TRANSLATION_HISTORY.table.table.header.loadDate,
    key: 'createdAt',
    class: 'table-hide-tablet',
    isSortable: true,
    render: (order: IRewardsOrder) => {
      return <div>{order.createdAt ? order.createdAt.split('T')[0] : <EmptyDash />}</div>;
    }
  },
  {
    name: TRANSLATION_HISTORY.table.table.header.status,
    key: 'status',
    class: 'table-hide-tablet',
    render: (order: IRewardsOrder) => {
      return (
        <Tag
          text={t(TRANSLATION_HISTORY.table.status[order.status])}
          type={REWARDS_ORDER_STATUS_TAG_TYPES[order.status]}
        />
      );
    }
  },
  {
    name: TRANSLATION_HISTORY.table.table.header.amount,
    key: 'amount',
    class: 'w-1',
    render: (order: IRewardsOrder) => {
      return (
        <div className="flex flex-row items-center gap-1">
          <GreenArrow />
          {order.amount}
        </div>
      );
    }
  }
];

const RewardsHistoryTable = ({ ordersList, loading, error, orderType }: RewardsHistoryTableProps) => {
  const [orders, setOrders] = useState<IRewardsOrder[] | undefined>(undefined);
  const [paginatorState, setPaginatorState] = useState<number>(DEFAULT_PAGINATOR_STATE);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentPageData, setCurrentPageData] = useState<IRewardsOrder[] | undefined>(undefined);
  const [isAscending, setIsAscending] = useState<boolean>(true);
  const [columns, setColumns] = useState<ITableColumn[]>([...REWARDS_OUTGOINGS_TABLE_COLUMNS]);
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const getTotalOrders = () => {
    return ordersList ? ordersList.length : 0;
  };

  const sort = () => {
    setOrders([...orders!].reverse());
    setIsAscending(!isAscending);
  };

  const paginateTransactions = () => {
    const from = currentPage * paginatorState;
    const to = from + paginatorState;
    const pageData = orders?.slice(from, to);
    setCurrentPageData(pageData);
  };

  const onPageChange = (index: number) => {
    setCurrentPage(index);
  };

  const onRowsPerPageChange = (value: number) => {
    setPaginatorState(value);
    setCurrentPage(0);
  };

  const changeTableColumns = () => {
    if (orderType == REWARDS_ORDER_TYPES.outgoings) {
      const tmpColumns = setCustomColumnsRender([...REWARDS_OUTGOINGS_TABLE_COLUMNS]);
      setColumns(tmpColumns);
    } else {
      setColumns([...REWARDS_DEPOSITS_TABLE_COLUMNS]);
    }
  };

  const setCustomColumnsRender = (columns: any[]) => {
    columns[4].render = (order: IRewardsOrder) => {
      return (
        <Tag
          text={t(TRANSLATION_HISTORY.table.status[order.status])}
          type={REWARDS_ORDER_STATUS_TAG_TYPES[order.status]}
          tooltip={{
            placement: 'top',
            tooltipTitle: <div className="tooltip-text"> {t(REWARDS_ORDER_STATUS_FILTER[order.status].tooltip!)} </div>,
            children: <InfoIcon className="tag-info-icon" />
          }}
        />
      );
    };

    return columns;
  };

  useEffect(() => {
    setCurrentPageData(undefined);
    if (getTotalOrders() > 0) {
      paginateTransactions();
    }
  }, [orders]);

  useEffect(() => {
    paginateTransactions();
  }, [currentPage, paginatorState]);

  useEffect(() => {
    setCurrentPageData(undefined);
    if (isMounted) {
      if (!ordersList) return;
      setOrders(
        ordersList.sort((a, b) => {
          if (a?.createdAt === undefined || a?.createdAt === null) return 0;
          if (b?.createdAt === undefined || b?.createdAt === null) return 0;
          const date1 = new Date(invertDateFormat(a.createdAt, '/', '-'));
          const date2 = new Date(invertDateFormat(b.createdAt, '/', '-'));
          return date2.getTime() - date1.getTime();
        })
      );
    }
  }, [ordersList, isMounted]);

  useEffect(() => {
    changeTableColumns();
  }, [orderType]);

  return (
    <>
      {(!loading && getTotalOrders() === 0) || error ? (
        <CustomEmptyPage
          CustomImage={EmptyBox}
          title={t(TRANSLATION_HISTORY.error.title)}
          subtitle={t(TRANSLATION_HISTORY.error.subtitle)}
          newExperience={true}
        />
      ) : (
        <>
          <div className="rewards-history-table">
            <StyledTable columns={columns} data={currentPageData} sortData={sort} error={error} />
          </div>
          <TablePagination
            rowsPerPageOptions={[5, 15, 25, 50]}
            rowsPerPage={paginatorState}
            count={getTotalOrders()}
            actualPage={currentPage}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            sectionPage={SECTION_REWARDS_HISTORY}
            marginTop={20}
          />
        </>
      )}
    </>
  );
};

export default RewardsHistoryTable;
